import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { XIcon, InformationCircleIcon, ChevronDownIcon, DotsVerticalIcon, PlayIcon, CogIcon, TrashIcon, PencilIcon, BadgeCheckIcon } from '@heroicons/react/outline';
import { colors, spacing } from '../../design-system';
import { message as antMessage, Tooltip, Dropdown } from 'antd';
import ReactMarkdown from 'react-markdown';
import type { CellData, TableColumn } from './types';
import type { Creator } from '../Creators/creatorTypes';
import { getTemplatePreview, type TemplateContext, TemplateService } from './templateEngine';
import { TikTokIcon, InstagramIcon } from '../../design-system/components/data-display/EmailProviderIcons';
import { TikTokVideoView, InstagramPostView, EmailThreadView } from './ExpandedSourceViews';

interface CellDetailsSidebarProps {
  isOpen: boolean;
  cellData: CellData | null;
  column: TableColumn | null;
  onClose: () => void;
  // Additional props for outreach functionality
  creator?: Creator;
  columns?: TableColumn[];
  rowData?: Record<string, CellData>;
  // Track posts props
  tableId?: string;
  onVerifyPost?: (verification: 'verified' | 'rejected' | 'pending_verification') => void;
  // Creator actions
  onRunFullRow?: (creatorId: string) => void;
  onEditCreator?: (creator: Creator) => void;
  onRemoveCreator?: (creatorId: string) => void;
}

const DrawerContainer = styled.div<{ $isOpen: boolean; $hasExpandedSource: boolean }>`
  position: relative;
  height: 100%;
  width: ${props => props.$isOpen ? (props.$hasExpandedSource ? '50%' : '40%') : '0%'};
  min-width: ${props => props.$isOpen ? (props.$hasExpandedSource ? '800px' : '500px') : '0px'};
  max-width: ${props => props.$hasExpandedSource ? '1000px' : '700px'};
  background: white;
  border-left: 1px solid ${colors.border.secondary};
  overflow: hidden;
  z-index: 10;
  display: flex;
  flex-direction: row;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: white;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  min-height: 38px;
`;

const DrawerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const TitleText = styled.h3`
  margin: 0;
  font-size: 11px;
  font-weight: 500;
  color: ${colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderAvatar = styled.div<{ $platform?: string; $hasImage?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: ${props => {
    if (props.$hasImage) return 'transparent';
    switch (props.$platform?.toLowerCase()) {
      case 'tiktok': return '#fe2c55';
      case 'instagram': return '#E4405F';
      case 'twitch': return '#9146FF';
      default: return '#6b7280';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  font-weight: 600;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
`;

const HeaderProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
`;

const HeaderAvatarFallback = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: white;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 2px;
  border-radius: 3px;
  transition: color 0.12s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #1a1a1a;
    background: rgba(0, 0, 0, 0.02);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
  
  svg {
    width: 12px;
    height: 12px;
    color: #6b7c93;
  }
`;

const MainContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid ${colors.border.secondary};
`;

const DrawerContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  padding: 0;
  
  /* Custom scrollbar like ColumnConfigDrawer */
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.15);
  }
`;

const ExpandedSourcePanel = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  background: ${colors.background.secondary};
  position: relative;
`;

const ExpandedSourceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 10px;
  background: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const ExpandedSourceTitle = styled.h3`
  margin: 0;
  font-size: 11px;
  font-weight: 500;
  color: ${colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ExpandedSourceContent = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ColumnSection = styled.div`
  padding: 24px 20px;
  background: ${colors.background.primary};
  margin: 0;
  border-bottom: 1px solid ${colors.border.secondary};
  
  &:first-child {
    padding-top: 28px;
  }
  
  &:last-child {
    padding-bottom: 28px;
    border-bottom: none;
  }
`;

const ColumnSectionHeader = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ColumnActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ColumnActionButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 3px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  color: #6b7280;
  
  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: #374151;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const SourceContainer = styled.div`
  margin-top: 12px;
  padding: 16px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
`;

const SourceLabel = styled.div`
  font-size: 10px;
  font-weight: 400;
  color: ${colors.text.tertiary};
  margin-bottom: 8px;
  opacity: 0.8;
`;

const SourceContent = styled.div`
  font-size: 13px;
  color: #525252;
  line-height: 1.5;
  max-height: 120px;
  overflow-y: auto;
  font-family: ui-monospace, 'SF Mono', 'Monaco', 'Cascadia Code', 'Consolas', monospace;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-word;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(107, 124, 147, 0.2);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(107, 124, 147, 0.3);
  }
`;

// Form Components matching ColumnConfigDrawer
const FormSection = styled.div`
  margin-bottom: ${spacing[6]};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const FieldGroup = styled.div`
  margin-bottom: ${spacing[5]};
  
  &:last-child {
    margin-bottom: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
  }
`;

const Label = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: ${colors.text.secondary};
  margin-bottom: 8px;
`;

const AnswerDisplay = styled.div`
  font-size: 14px;
  color: ${colors.text.primary};
  line-height: 1.5;
  white-space: pre-wrap;
  margin-top: 4px;
`;

const VerificationButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const VerifyButton = styled.button<{ $variant: 'verify' | 'reject' }>`
  flex: 1;
  padding: 10px 16px;
  border-radius: 6px;
  border: none;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  
  ${props => props.$variant === 'verify' ? `
    background: rgba(16, 185, 129, 0.08);
    color: #059669;
    
    &:hover {
      background: rgba(16, 185, 129, 0.15);
    }
  ` : `
    background: rgba(239, 68, 68, 0.08);
    color: #dc2626;
    
    &:hover {
      background: rgba(239, 68, 68, 0.15);
    }
  `}
  
  &:active {
    transform: scale(0.98);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QueryDisplay = styled.div`
  font-size: 12px;
  color: ${colors.text.tertiary};
  font-style: italic;
  margin-bottom: 12px;
  opacity: 0.8;
`;

const SourcesSection = styled.div`
  margin-top: ${spacing[8]};
  padding-top: ${spacing[6]};
  border-top: 1px solid ${colors.border.secondary};
`;

const SourcesList = styled.div`
  margin-top: ${spacing[3]};
`;

const SourceItem = styled.div`
  padding: ${spacing[3]} 0;
  border-bottom: 1px solid ${colors.border.secondary};
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const SourceTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 4px;
`;

const EmailSourceCard = styled.div`
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 16px;
  margin-top: 12px;
  font-family: ui-monospace, 'SF Mono', Monaco, 'Cascadia Code', Consolas, monospace;
  font-size: 13px;
  line-height: 1.5;
  color: #1a1a1a;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
  
  /* Email header styling */
  .email-header {
    border-bottom: 1px solid #e6e6e6;
    padding-bottom: 12px;
    margin-bottom: 16px;
    font-weight: 500;
  }
  
  .email-content {
    color: #525252;
    line-height: 1.6;
  }
`;

const EmailToggle = styled.button`
  background: none;
  border: none;
  color: #0066cc;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s ease;
  
  &:hover {
    color: #0052a3;
  }
  
  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.2s ease;
  }
  
  &.expanded svg {
    transform: rotate(180deg);
  }
`;

const SourceUrl = styled.a`
  font-size: 11px;
  color: #737373;
  text-decoration: none;
  display: block;
  margin-bottom: 4px;
  word-break: break-all;
  
  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
`;

const SourceDescription = styled.div`
  font-size: 10px;
  color: ${colors.text.tertiary};
  line-height: 1.4;
  opacity: 0.8;
`;

const PlatformSourceItem = styled.div`
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  margin-bottom: 8px;
  background: #fafafa;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const PlatformSourceHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[1]};
`;

const PlatformIconWrapper = styled.div`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlatformSourceTitle = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.text.primary};
  flex: 1;
  line-height: 1.4;
`;

const PlatformSourceMeta = styled.div`
  font-size: 10px;
  color: ${colors.text.tertiary};
  margin-left: 24px;
  margin-top: 2px;
  opacity: 0.8;
`;

const ConfidenceBadge = styled.span<{ $confidence: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 400;
  text-transform: lowercase;
  background: ${colors.background.secondary};
  color: ${colors.text.tertiary};
  border: 1px solid ${colors.border.secondary};
  opacity: 0.8;
`;

const EmptyState = styled.div`
  text-align: center;
  color: ${colors.text.tertiary};
  font-size: 13px;
  padding: 40px 20px;
  opacity: 0.6;
`;

const TemplatePreview = styled.div`
  background: ${colors.background.secondary};
  border: 1px solid ${colors.border.secondary};
  border-radius: 8px;
  padding: ${spacing[4]};
  font-size: 13px;
  color: ${colors.text.primary};
  line-height: 1.5;
  flex: 1;
  overflow-y: auto;
  white-space: pre-wrap;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;


const CitationMarker = styled.span<{ $isExpanded?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 16px;
  padding: 0 4px;
  font-size: 10px;
  font-weight: 600;
  color: ${props => props.$isExpanded ? 'white' : colors.palette.indigo[700]};
  background: ${props => props.$isExpanded ? colors.palette.indigo[600] : 'white'};
  border: 1px solid ${props => props.$isExpanded ? colors.palette.indigo[600] : colors.palette.indigo[200]};
  border-radius: 3px;
  margin: 0 1px;
  cursor: pointer;
  transition: all 0.15s ease;
  vertical-align: middle;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.$isExpanded ? colors.palette.indigo[700] : colors.palette.indigo[50]};
    border-color: ${props => props.$isExpanded ? colors.palette.indigo[700] : colors.palette.indigo[400]};
    box-shadow: 0 1px 3px rgba(99, 102, 241, 0.1);
  }
`;

const CitationTooltipContent = styled.div`
  max-width: 380px;
  padding: 0;
  display: flex;
  gap: ${spacing[3]};
`;

const CitationThumbnail = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
  background: ${colors.palette.gray[100]};
`;

const CitationTextContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CitationTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: 8px;
  line-height: 1.5;
`;

const CitationMeta = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 10px;
  line-height: 1.5;
`;

const CitationLink = styled.a`
  font-size: 12px;
  color: ${colors.palette.indigo[600]};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
    color: ${colors.palette.indigo[700]};
  }
`;

const ReasoningCard = styled.div`
  margin-top: 0;
`;

const AnswerLabel = styled.div`
  font-size: 10px;
  font-weight: 400;
  color: ${colors.text.tertiary};
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0.8;
`;

const ReasoningLabel = styled.div`
  font-size: 10px;
  font-weight: 400;
  color: ${colors.text.tertiary};
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0.8;
`;

const ReasoningDescription = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  margin-bottom: 12px;
  opacity: 0.8;
`;

const ReasoningTextContainer = styled.div`
  border-radius: 0;
  padding: 0;
  background: transparent;
  border: none;
`;

const ReasoningText = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  
  /* Markdown styling */
  p {
    margin: 0 0 ${spacing[3]} 0;
    white-space: normal;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong {
    font-weight: 600;
    color: ${colors.text.primary};
  }
  
  ul, ol {
    margin: ${spacing[2]} 0;
    padding-left: ${spacing[5]};
    white-space: normal;
  }
  
  li {
    margin-bottom: ${spacing[2]};
    white-space: normal;
  }
  
  code {
    background: ${colors.palette.gray[100]};
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 12px;
    font-family: 'Courier New', monospace;
  }
`;


export const CellDetailsSidebar: React.FC<CellDetailsSidebarProps> = ({
  isOpen,
  cellData,
  column,
  onClose,
  creator,
  columns = [],
  rowData = {},
  tableId,
  onVerifyPost,
  onRunFullRow,
  onEditCreator,
  onRemoveCreator
}) => {
  const [processedTemplate, setProcessedTemplate] = useState<string>('');
  const [processedSubject, setProcessedSubject] = useState<string>('');
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [expandedSource, setExpandedSource] = useState<any>(null);
  const [avatarImageError, setAvatarImageError] = useState(false);
  const [reviewedColumns, setReviewedColumns] = useState<Set<string>>(new Set());

  // Get creator display name
  const getCreatorDisplayName = (): string => {
    if (!creator) return 'Creator';
    
    // Try TikTok handle first
    if (creator.tiktok_data && creator.tiktok_data.length > 0) {
      return `@${creator.tiktok_data[0].handle}`;
    }
    
    // Try Instagram handle
    if (creator.instagram_data && creator.instagram_data.length > 0) {
      return `@${creator.instagram_data[0].handle}`;
    }
    
    // Try Twitch handle
    if (creator.twitch_data && creator.twitch_data.length > 0) {
      return `@${creator.twitch_data[0].handle}`;
    }
    
    // Fallback to shipping name
    if (creator.creator_shipping?.name) {
      return creator.creator_shipping.name;
    }
    
    return 'Creator';
  };

  // Get creator's primary platform data for avatar
  const getCreatorAvatarData = (): { 
    profileImageUrl?: string; 
    platform?: string; 
    name?: string;
  } => {
    if (!creator) return {};
    
    // Try TikTok first
    if (creator.tiktok_data && creator.tiktok_data.length > 0) {
      return {
        profileImageUrl: creator.tiktok_data[0].profile_image_url,
        platform: 'tiktok',
        name: creator.tiktok_data[0].handle
      };
    }
    
    // Try Instagram
    if (creator.instagram_data && creator.instagram_data.length > 0) {
      return {
        profileImageUrl: creator.instagram_data[0].profile_image_url,
        platform: 'instagram',
        name: creator.instagram_data[0].handle
      };
    }
    
    // Try Twitch
    if (creator.twitch_data && creator.twitch_data.length > 0) {
      return {
        profileImageUrl: creator.twitch_data[0].profile_image_url,
        platform: 'twitch',
        name: creator.twitch_data[0].handle
      };
    }
    
    return {
      name: creator.creator_shipping?.name || 'Creator'
    };
  };

  // Reset expanded source and avatar error when sidebar closes
  useEffect(() => {
    if (!isOpen) {
      setExpandedSource(null);
      setAvatarImageError(false);
      setReviewedColumns(new Set());
    }
  }, [isOpen]);

  // Reset avatar error and reviewed columns when creator changes
  useEffect(() => {
    setAvatarImageError(false);
    setReviewedColumns(new Set());
  }, [creator?.id]);

  // Process outreach template when sidebar opens for outreach columns
  useEffect(() => {
    const loadAndProcessTemplate = async () => {
      if (false && creator) {
        setLoadingTemplate(true);
        try {
          console.log('🔄 [CellDetailsSidebar] Processing outreach template');
          
          const templateContext: TemplateContext = {
            creator: creator!,
            columns,
            rowData
          };

          // Get the primary template from the column
          const primaryTemplate = column ? TemplateService.getPrimaryTemplate(column!) : null;
          
          if (primaryTemplate) {
            console.log('📄 [CellDetailsSidebar] Processing template:', primaryTemplate.title || 'Untitled');
            
            // Process template content using the shared service
            const templateContent = primaryTemplate.content || primaryTemplate.plain_text;
            const processed = TemplateService.processTemplateContent(templateContent, templateContext);
            
            setProcessedTemplate(processed.processedText);
            console.log('✅ [CellDetailsSidebar] Processed template successfully');
            
            if (processed.missingVariables.length > 0) {
              console.log('⚠️ [CellDetailsSidebar] Missing variables:', processed.missingVariables);
            }
          } else {
            setProcessedTemplate('No template content available');
            console.log('⚠️ [CellDetailsSidebar] No template found for column');
          }

          // Process subject template (if available)
          if (false) {
            const subjectPreview = getTemplatePreview('', templateContext);
            setProcessedSubject(subjectPreview);
          }
        } catch (error) {
          console.error('❌ [CellDetailsSidebar] Error loading template:', error);
          setProcessedTemplate('Error loading template');
        } finally {
          setLoadingTemplate(false);
        }
      } else {
        setProcessedTemplate('');
        setProcessedSubject('');
      }
    };

    loadAndProcessTemplate();
  }, [isOpen, column, creator, columns, rowData]);

  // Copy to clipboard functionality
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      antMessage.success(`${label} copied to clipboard`);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      antMessage.error('Failed to copy to clipboard');
    }
  };

  // Process reasoning text to handle bullet points and formatting
  const processReasoning = (reasoning: string) => {
    if (!reasoning) return reasoning;
    
    // Convert bullet points and numbered lists to proper HTML
    let processed = reasoning
      // Handle bullet points with •, -, or *
      .replace(/^[•\-\*]\s+(.+)$/gm, '<li>$1</li>')
      // Handle numbered lists
      .replace(/^\d+\.\s+(.+)$/gm, '<li>$1</li>')
      // Handle line breaks
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br/>');
    
    // Wrap in proper list tags if we have list items
    if (processed.includes('<li>')) {
      processed = processed.replace(/(<li>[\s\S]*<\/li>)/g, '<ul>$1</ul>');
    }
    // Wrap in paragraph tags if not already wrapped
    if (!processed.includes('<p>') && !processed.includes('<ul>')) {
      processed = `<p>${processed}</p>`;
    }
    
    return processed;
  };

  // Parse sources if they exist
  const parseSources = (sourcesData?: string) => {
    if (!sourcesData || sourcesData === 'none') return [];
    
    try {
      const parsed = JSON.parse(sourcesData);
      if (Array.isArray(parsed)) {
        return parsed.map(source => ({
          title: source.title || 'Source',
          url: source.url || '',
          type: source.type || 'generic',
          description: source.description || ''
        }));
      }
      return [{ title: 'Source', url: sourcesData, type: 'generic', description: '' }];
    } catch {
      return [{ title: 'Source', url: sourcesData, type: 'generic', description: '' }];
    }
  };


  // Check if sources are platform-related or email citations (TikTok, Instagram, email messages, etc.)
  const isPlatformSources = (sources: any[]) => {
    if (sources.length === 0) return false;
    const platformTypes = ['tiktok_video', 'tiktok_profile', 'tiktok_analytics', 'instagram_post', 'instagram_profile', 'instagram_analytics', 'platform_resource', 'google_drive_document', 'email_message'];
    return sources.some(source => platformTypes.includes(source.type));
  };

  // Render platform icon component based on source type
  const renderPlatformIcon = (type: string) => {
    if (type.startsWith('tiktok')) {
      return <TikTokIcon size={16} />;
    }
    if (type.startsWith('instagram')) {
      return <InstagramIcon size={16} />;
    }
    // Generic fallback - no icon
    return null;
  };

  const sources = parseSources(cellData?.source);
  const hasPlatformSources = isPlatformSources(sources);

  // Handle citation click to expand source
  const handleCitationClick = (source: any) => {
    console.log('🔍 [Citation Click] Source:', source);
    console.log('🔍 [Citation Click] Type:', source.type);
    console.log('🔍 [Citation Click] URL:', source.url);
    console.log('🔍 [Citation Click] Thumbnail:', source.thumbnail);
    
    if (expandedSource && expandedSource.url === source.url) {
      // If clicking the same source, collapse it
      setExpandedSource(null);
    } else {
      // Expand the new source
      setExpandedSource(source);
    }
  };

  // Check if we have messages to display (from new AI response format)
  const messages = cellData?.messages || [];
  const highlightTextValue = cellData?.highlight_text || '';

  // Extract creator profile image and emails for EmailThreadView
  const creatorProfileImage = creator?.tiktok_data?.[0]?.profile_image_url || 
                               creator?.instagram_data?.[0]?.profile_image_url || 
                               creator?.twitch_data?.[0]?.profile_image_url || 
                               undefined;
  const creatorEmails = creator?.contacts?.filter(c => c.type === 'email').map(c => c.value) || [];

  // Helper to render reasoning with inline citations and markdown
  const renderReasoningWithCitations = (reasoning: string, sources: any[]) => {
    if (!reasoning) return null;
    
    // Convert single newlines to double newlines for proper markdown rendering
    // But preserve existing double newlines
    const processedReasoning = reasoning
      .replace(/\n\n/g, '___DOUBLE_NEWLINE___')  // Temporarily replace double newlines
      .replace(/\n/g, '\n\n')  // Convert single newlines to double
      .replace(/___DOUBLE_NEWLINE___/g, '\n\n\n');  // Restore double newlines (now triple)
    
    // Custom markdown components to handle inline citations
    const components = {
      p: ({ children }: any) => {
        // Process children to inject citation markers
        return <p>{processCitationsInChildren(children, sources)}</p>;
      },
      li: ({ children }: any) => {
        return <li>{processCitationsInChildren(children, sources)}</li>;
      }
    };
    
    return <ReactMarkdown components={components}>{processedReasoning}</ReactMarkdown>;
  };
  
  // Helper to process citations within markdown children
  const processCitationsInChildren = (children: any, sources: any[]): any => {
    if (typeof children === 'string') {
      // Parse citations in the string
      const citationRegex = /\[(\d+)\]/g;
      const parts: (string | JSX.Element)[] = [];
      let lastIndex = 0;
      let match;
      
      while ((match = citationRegex.exec(children)) !== null) {
        const citationNum = parseInt(match[1], 10);
        const citationIndex = citationNum - 1;
        
        // Add text before citation
        if (match.index > lastIndex) {
          parts.push(children.substring(lastIndex, match.index));
        }
        
        // Add citation marker
        const source = sources[citationIndex];
        if (source) {
          const isEmailSource = source.type === 'email_message';
          const isExpanded = expandedSource && expandedSource.url === source.url;
          
          const tooltipContent = (
            <CitationTooltipContent>
              {source.thumbnail && (
                <CitationThumbnail src={source.thumbnail} alt={source.title} />
              )}
              <CitationTextContent>
                {isEmailSource ? (
                  <>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: colors.text.primary, marginBottom: '8px', lineHeight: 1.4 }}>
                      {source.title}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.text.tertiary, marginBottom: '4px', lineHeight: 1.4 }}>
                      <strong style={{ color: colors.text.secondary, fontWeight: 500 }}>From:</strong> {source.sender}
                    </div>
                    {source.to && (
                      <div style={{ fontSize: '11px', color: colors.text.tertiary, marginBottom: '4px', lineHeight: 1.4 }}>
                        <strong style={{ color: colors.text.secondary, fontWeight: 500 }}>To:</strong> {source.to}
                      </div>
                    )}
                    <div style={{ fontSize: '10px', color: colors.text.tertiary, marginBottom: '10px', lineHeight: 1.4, opacity: 0.8 }}>
                      {source.date}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.text.secondary, lineHeight: 1.5, paddingTop: '8px', borderTop: `1px solid ${colors.border.secondary}` }}>
                      {source.description}
                    </div>
                  </>
                ) : (
                  <>
                    <CitationTitle>{source.title}</CitationTitle>
                    <CitationMeta>{source.description}</CitationMeta>
                    {source.url && (
                      <div style={{ fontSize: '11px', color: colors.text.tertiary, marginTop: '8px' }}>
                        Click to {isExpanded ? 'collapse' : 'expand'} full content
                      </div>
                    )}
                  </>
                )}
              </CitationTextContent>
            </CitationTooltipContent>
          );
          
          parts.push(
            <Tooltip 
              key={`citation-${match.index}`}
              title={tooltipContent}
              placement="top"
              overlayStyle={{ maxWidth: isEmailSource ? '480px' : '400px' }}
              overlayInnerStyle={{
                backgroundColor: 'white',
                color: colors.text.primary,
                padding: '14px 16px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)',
              }}
              color="white"
            >
              <CitationMarker 
                $isExpanded={isExpanded}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleCitationClick(source);
                }}
              >
                {citationNum}
              </CitationMarker>
            </Tooltip>
          );
        }
        
        lastIndex = match.index + match[0].length;
      }
      
      // Add remaining text
      if (lastIndex < children.length) {
        parts.push(children.substring(lastIndex));
      }
      
      return parts.length > 0 ? parts : children;
    }
    
    // If children is an array, process each child
    if (Array.isArray(children)) {
      return children.map((child, i) => 
        typeof child === 'string' ? processCitationsInChildren(child, sources) : child
      );
    }
    
    return children;
  };

  // Helper to render expanded source content
  const renderExpandedSource = () => {
    if (!expandedSource) return null;
    
    const isEmailSource = expandedSource.type === 'email_message';
    const isTikTokVideo = expandedSource.type.startsWith('tiktok_video');
    const isInstagramPost = expandedSource.type.startsWith('instagram_post');
    
    console.log('🎨 [Render Expanded] Type:', expandedSource.type);
    console.log('🎨 [Render Expanded] isInstagramPost:', isInstagramPost);
    console.log('🎨 [Render Expanded] isTikTokVideo:', isTikTokVideo);
    console.log('🎨 [Render Expanded] isEmailSource:', isEmailSource);
    console.log('🎨 [Render Expanded] Messages available:', messages.length);
    
    return (
      <ExpandedSourcePanel>
        <ExpandedSourceHeader>
          <ExpandedSourceTitle>
            {isEmailSource ? 'Email Thread' : isInstagramPost ? 'Instagram Post' : isTikTokVideo ? 'TikTok Video' : 'Content'}
          </ExpandedSourceTitle>
          <CloseButton onClick={() => setExpandedSource(null)}>
            <XIcon />
          </CloseButton>
        </ExpandedSourceHeader>
        
        <ExpandedSourceContent>
          {isEmailSource ? (
            <EmailThreadView
              messages={messages}
              highlightText={highlightTextValue}
              creatorProfileImage={creatorProfileImage}
              creatorEmails={creatorEmails}
            />
          ) : isInstagramPost ? (
            <InstagramPostView
              url={expandedSource.url}
              title={expandedSource.title}
              description={expandedSource.description}
            />
          ) : isTikTokVideo ? (
            <TikTokVideoView
              url={expandedSource.url}
              title={expandedSource.title}
              description={expandedSource.description}
            />
          ) : (
            // Fallback for other content types
            <div style={{ padding: '20px', background: 'white', borderRadius: '8px', border: `1px solid ${colors.border.secondary}` }}>
              <div style={{ fontSize: '14px', fontWeight: 500, color: colors.text.primary, marginBottom: '12px' }}>
                {expandedSource.title}
              </div>
              {expandedSource.description && (
                <div style={{ fontSize: '13px', color: colors.text.secondary, lineHeight: 1.6, marginBottom: '12px' }}>
                  {expandedSource.description}
                </div>
              )}
              {expandedSource.url && (
                <a 
                  href={expandedSource.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    fontSize: '12px', 
                    color: colors.palette.indigo[600], 
                    textDecoration: 'none'
                  }}
                >
                  View source →
                </a>
              )}
            </div>
          )}
        </ExpandedSourceContent>
      </ExpandedSourcePanel>
    );
  };

  // Helper to parse and format outreach cell data
  const formatOutreachData = (value: string) => {
    try {
      const data = JSON.parse(value);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {data.status && (
            <div>
              <div style={{ fontSize: '11px', color: colors.text.tertiary, marginBottom: '4px' }}>Status</div>
              <div style={{ fontSize: '13px', color: colors.text.primary, textTransform: 'capitalize' }}>{data.status}</div>
            </div>
          )}
          {data.sentAt && (
            <div>
              <div style={{ fontSize: '11px', color: colors.text.tertiary, marginBottom: '4px' }}>Sent At</div>
              <div style={{ fontSize: '13px', color: colors.text.primary }}>
                {new Date(data.sentAt).toLocaleString('en-US', { 
                  month: 'short', 
                  day: 'numeric', 
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          )}
          {data.sentTo && (
            <div>
              <div style={{ fontSize: '11px', color: colors.text.tertiary, marginBottom: '4px' }}>Sent To</div>
              <div style={{ fontSize: '13px', color: colors.text.primary }}>{data.sentTo}</div>
            </div>
          )}
          {data.subject && (
            <div>
              <div style={{ fontSize: '11px', color: colors.text.tertiary, marginBottom: '4px' }}>Subject</div>
              <div style={{ fontSize: '13px', color: colors.text.primary }}>{data.subject}</div>
            </div>
          )}
          {data.personalizedContent && (
            <div>
              <div style={{ fontSize: '11px', color: colors.text.tertiary, marginBottom: '4px' }}>Message</div>
              <div style={{ fontSize: '13px', color: colors.text.primary, whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                {data.personalizedContent}
              </div>
            </div>
          )}
        </div>
      );
    } catch (e) {
      return value;
    }
  };

  // Helper to render cell content for each column
  const renderColumnContent = (col: TableColumn, cellData: CellData | undefined) => {
    if (!cellData || (!cellData.value && !cellData.loading && !cellData.error)) {
      return (
        <div style={{ color: '#a3a3a3', fontSize: '13px' }}>
          No data
        </div>
      );
    }
    
    if (cellData.loading) {
      return (
        <div style={{ color: '#a3a3a3', fontSize: '13px' }}>
          Loading...
        </div>
      );
    }
    
    if (cellData.error) {
      return (
        <div style={{ color: '#dc2626', fontSize: '13px' }}>
          {cellData.error}
        </div>
      );
    }
    
    // Handle outreach column type
    if (col.cell_type === 'outreach') {
      return (
        <div>
          {formatOutreachData(cellData.value)}
        </div>
      );
    }
    
    // Parse sources
    const sources = parseSources(cellData?.source);
    const hasPlatformSources = isPlatformSources(sources);
    
    // Show the value
    return (
      <div>
        <div style={{ fontSize: '14px', color: colors.text.primary, lineHeight: '1.6', whiteSpace: 'pre-wrap', marginBottom: cellData.reasoning ? '16px' : '0' }}>
          {cellData.value}
        </div>
        
        {/* Show reasoning with inline citations */}
        {cellData.reasoning && (
          <ReasoningCard>
            <ReasoningLabel>Reasoning</ReasoningLabel>
            <ReasoningTextContainer>
              <ReasoningText>
                {hasPlatformSources ? (
                  renderReasoningWithCitations(cellData.reasoning, sources)
                ) : (
                  <ReactMarkdown>
                    {cellData.reasoning.replace(/\n\n/g, '___DOUBLE_NEWLINE___').replace(/\n/g, '\n\n').replace(/___DOUBLE_NEWLINE___/g, '\n\n\n')}
                  </ReactMarkdown>
                )}
              </ReasoningText>
            </ReasoningTextContainer>
          </ReasoningCard>
        )}
      </div>
    );
  };

  return (
    <DrawerContainer $isOpen={isOpen} $hasExpandedSource={!!expandedSource}>
      {/* Main content panel (left side or full width if no expanded source) */}
      <MainContentWrapper>
        <DrawerHeader>
          <DrawerTitle>
            <CloseButton onClick={onClose}>
              <XIcon />
            </CloseButton>
            {(() => {
              const avatarData = getCreatorAvatarData();
              const hasValidUrl = avatarData.profileImageUrl && typeof avatarData.profileImageUrl === 'string' && avatarData.profileImageUrl.trim() !== '';
              const hasImage = !!(hasValidUrl && !avatarImageError);
              
              return (
                <HeaderAvatar $platform={avatarData.platform} $hasImage={hasImage}>
                  {hasImage ? (
                    <>
                      <HeaderProfileImage
                        src={avatarData.profileImageUrl}
                        alt={`${avatarData.name} profile`}
                        onError={() => setAvatarImageError(true)}
                      />
                      <HeaderAvatarFallback style={{ opacity: 0 }}>
                        {(avatarData.name?.charAt(0) || '?').toUpperCase()}
                      </HeaderAvatarFallback>
                    </>
                  ) : (
                    <HeaderAvatarFallback>
                      {(avatarData.name?.charAt(0) || '?').toUpperCase()}
                    </HeaderAvatarFallback>
                  )}
                </HeaderAvatar>
              );
            })()}
            <TitleText>
              {getCreatorDisplayName()}
            </TitleText>
          </DrawerTitle>
          
          {/* Creator actions dropdown */}
          {creator && (
            <Dropdown
              menu={{
                items: [
                  ...(onRunFullRow ? [{
                    key: 'run-row',
                    label: 'Run full row',
                    icon: <PlayIcon style={{ width: '14px', height: '14px' }} />,
                    onClick: (e: any) => {
                      e?.domEvent?.stopPropagation();
                      onRunFullRow(creator.id);
                      antMessage.success('Running all columns for creator');
                    }
                  }] : []),
                  ...(onEditCreator ? [{
                    key: 'edit',
                    label: 'Edit creator details',
                    icon: <CogIcon style={{ width: '14px', height: '14px' }} />,
                    onClick: (e: any) => {
                      e?.domEvent?.stopPropagation();
                      onEditCreator(creator);
                    }
                  }] : []),
                  ...(onRemoveCreator ? [{
                    key: 'delete',
                    label: 'Remove from project',
                    icon: <TrashIcon style={{ width: '14px', height: '14px' }} />,
                    onClick: (e: any) => {
                      e?.domEvent?.stopPropagation();
                      onRemoveCreator(creator.id);
                    },
                    danger: true
                  }] : [])
                ],
                onClick: (e: any) => {
                  e.domEvent.stopPropagation();
                }
              }}
              placement="bottomRight"
            >
              <ActionButton onClick={(e) => e.stopPropagation()} style={{ padding: '3px' }}>
                <DotsVerticalIcon style={{ width: '12px', height: '12px', color: '#6b7c93' }} />
              </ActionButton>
            </Dropdown>
          )}
        </DrawerHeader>

        <DrawerContent>
          {columns.map((col) => (
            <ColumnSection key={col.id}>
              <ColumnSectionHeader>
                <span>{col.name}</span>
                <ColumnActions>
                  <Tooltip title="Edit column">
                    <ColumnActionButton onClick={() => {
                      // TODO: Open column config drawer
                      console.log('Edit column:', col.id);
                    }}>
                      <PencilIcon />
                    </ColumnActionButton>
                  </Tooltip>
                  <Tooltip title={reviewedColumns.has(col.id) ? "Verified" : "Mark as verified"}>
                    <ColumnActionButton 
                      onClick={() => {
                        setReviewedColumns(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(col.id)) {
                            newSet.delete(col.id);
                          } else {
                            newSet.add(col.id);
                          }
                          return newSet;
                        });
                      }}
                      style={{
                        color: reviewedColumns.has(col.id) ? '#10b981' : '#6b7280'
                      }}
                    >
                      <BadgeCheckIcon />
                    </ColumnActionButton>
                  </Tooltip>
                </ColumnActions>
              </ColumnSectionHeader>
              {renderColumnContent(col, rowData[col.id])}
            </ColumnSection>
          ))}
          {columns.length === 0 && (
            <div style={{ padding: spacing[5] }}>
              <EmptyState>
                <InformationCircleIcon style={{ width: '48px', height: '48px', margin: '0 auto 12px' }} />
                <div>No columns to display</div>
              </EmptyState>
            </div>
          )}
        </DrawerContent>
      </MainContentWrapper>
      
      {/* Expanded source panel (right side) */}
      {expandedSource && renderExpandedSource()}
    </DrawerContainer>
  );
};
